import React from 'react';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import { Col, Form, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import FormikInputField from '../../app/components/formikComponents/FormikInputField';
import FormikCountryInput from '../../app/components/formikComponents/FormikCountryInput';
import StyledButton from '../../app/components/styledComponents/StyledButton';
import { FieldArray, Formik } from 'formik';
import { customerDefaultValues, customerSchema } from './assets/customersUtil';
import {
  useAddCustomerMutation,
  useGetSingleCustomerQuery,
  useUpdateCustomerMutation,
} from './customersApiSlice';
import TrashIcon from '../../app/components/svgIcons/TrashIcon';
import FormikCheckBox from '../../app/components/formikComponents/FormikCheckBox';
import styled from 'styled-components';
import FormikAsyncMap from '../../app/components/formikComponents/FormikAsyncMap';
import { ShopStyled } from '../shopSetting/assets/shopStyled';
import NewArrowLeftIcon from '../../app/components/svgIcons/NewArrowLeftIcon';
import { isAuthorized, scrollToFirstError } from '../../utils/helper';
import { useSelector } from 'react-redux';
import { StyledTooltip as StyledTooltips } from '../../app/components/styledComponents/StyledTooltip';

const CreateCustomer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [addCustomerAPI, { isLoading: loading }] = useAddCustomerMutation();
  const [updateCustomerAPI, { isLoading: submitLoading }] =
    useUpdateCustomerMutation();

  const { data: singleCustomer } = useGetSingleCustomerQuery(id, {
    skip: !id,
    refetchOnMountOrArgChange: true,
  });

  const userPermissions = useSelector(
    (state) => state?.authorization?.userPermissions,
  );

  const has_permission_to_update_customer = isAuthorized(
    userPermissions,
    'can update customer',
  );

  const handleAddCustomer = async (_values) => {
    // TODO remove address field for consistency
    if (id && !isNaN(id) && !has_permission_to_update_customer) return;

    const values = { ..._values };
    values.zip = values?.zipCode || '';
    values.address1 = values?.address || '';
    const call = id ? updateCustomerAPI : addCustomerAPI;
    if (id) {
      values['id'] = id;
    }
    const { data } = await call(values);
    if (!data?.error) {
      toast.success(data?.description || 'Customer Saved Successfully');
      navigate('/customers');
    }
  };

  return (
    <ShopStyled.PageWrap isFullWidth>
      <Formik
        initialValues={{
          ...customerDefaultValues,
          ...(id && {
            ...singleCustomer?.objectData,
            zipCode: singleCustomer?.objectData?.zip || '',
            address: singleCustomer?.objectData?.address1 || '',
            companyName: singleCustomer?.objectData?.companyName || '',
            firstName: singleCustomer?.objectData?.firstName || '',
            lastName: singleCustomer?.objectData?.lastName || '',
            customerPhones: singleCustomer?.objectData?.customerPhones ?? [],
            customerEmails: singleCustomer?.objectData?.customerEmails || [],
            address2: singleCustomer?.objectData?.address1 || '',
            notes: singleCustomer?.objectData?.notes || '',
          }),
        }}
        enableReinitialize
        validationSchema={customerSchema}
        onSubmit={handleAddCustomer}
      >
        {({ handleSubmit, values, setFieldValue, validateForm }) => (
          <Form
            onSubmit={async (e) => {
              e.preventDefault();
              const formErrors = await validateForm();
              scrollToFirstError(formErrors);
              handleSubmit();
            }}
          >
            <ShopStyled.BootstrapCard className="p-0">
              <ShopStyled.NewPageTitle hasBorder>
                <div className="d-flex align-items-center">
                  <div className="heading heading24 ms-1 ms-md-2 d-flex align-items-center gap-2 gap-md-3">
                    <span className="back-icon" onClick={() => navigate(-1)}>
                      <NewArrowLeftIcon />
                    </span>
                    <span>{`${id ? 'Edit' : 'Add'} Customer`}</span>
                  </div>
                  -
                </div>
              </ShopStyled.NewPageTitle>
              <Row className="m-4">
                <Col sm={12} className="mb-2">
                  <label htmlFor={`companyName`} className="form-label">
                    Organization
                  </label>
                  <FormikInputField
                    name={`companyName`}
                    id={`companyName`}
                    placeholder="Enter organization name"
                  />
                </Col>
                <Col sm={6} className="mb-2">
                  <label htmlFor={`firstName`} className="form-label">
                    First Name
                  </label>
                  <FormikInputField
                    name={`firstName`}
                    id={`firstName`}
                    placeholder="Enter first name"
                  />
                </Col>
                <Col sm={6} className="mb-2">
                  <label htmlFor={`lastName`} className="form-label">
                    Last Name
                  </label>
                  <FormikInputField
                    name={`lastName`}
                    id={`lastName`}
                    placeholder="Enter last name"
                  />
                </Col>

                <FieldArray
                  name="customerPhones"
                  render={(arrayHelpers) => (
                    <Col sm={12} className="mb-2">
                      <label htmlFor={`firstName`} className="form-label">
                        Phone Numbers
                      </label>
                      <Row>
                        {Array.isArray(values?.customerPhones) &&
                          values?.customerPhones?.map((item, index) => (
                            <Col sm={4} key={index}>
                              <div key={index} className="mb-3 d-flex">
                                <div className="d-flex align-items-center flex-grow-1 gap-2 me-1 position-relative">
                                  <OverlayTrigger
                                    overlay={
                                      <StyledTooltip
                                        id={`customerPhones.${index}.default1`}
                                      >
                                        Set as default phone number
                                      </StyledTooltip>
                                    }
                                  >
                                    <span className="d-inline-block">
                                      <FormikCheckBox
                                        name={`customerPhones.${index}.default1`}
                                        onChange={() => {
                                          values.customerPhones =
                                            values?.customerPhones?.map(
                                              (ph, i) => ({
                                                ...ph,
                                                default1:
                                                  i === index &&
                                                  ph?.default1 !== 'true'
                                                    ? 'true'
                                                    : 'false',
                                              }),
                                            );
                                          setFieldValue(
                                            `customerPhones`,
                                            values.customerPhones,
                                          );
                                        }}
                                        id="customerPhones"
                                        value={
                                          item?.default1 === 'true'
                                            ? true
                                            : false
                                        }
                                      />
                                    </span>
                                  </OverlayTrigger>
                                  <FormikCountryInput
                                    name={`customerPhones.${index}.phone`}
                                    id={`customerPhones.${index}.phone`}
                                    placeholder="Enter phone number"
                                  />
                                </div>
                                {values?.customerPhones?.length > 1 && (
                                  <StyledButton
                                    className="py-0"
                                    variant="iconBackground"
                                    icon={<TrashIcon />}
                                    type={'button'}
                                    onClick={() => {
                                      arrayHelpers.remove(index);
                                    }}
                                  />
                                )}
                              </div>
                            </Col>
                          ))}
                      </Row>
                      <h6
                        className="text-primary fw-bold"
                        role="button"
                        onClick={() =>
                          arrayHelpers.insert(
                            values?.customerPhones?.length,
                            '',
                          )
                        }
                      >
                        + Add another phone
                      </h6>
                    </Col>
                  )}
                />

                <FieldArray
                  name="customerEmails"
                  render={(arrayHelpers) => (
                    <Col sm={12} className="mb-2">
                      <label htmlFor={`firstName`} className="form-label">
                        Emails
                      </label>
                      <Row>
                        {Array.isArray(values?.customerEmails) &&
                          values?.customerEmails?.map((item, index) => (
                            <Col sm={4} key={index}>
                              <div key={index} className="mb-3 d-flex">
                                <div className="d-flex align-items-center flex-grow-1 gap-2 me-1 position-relative">
                                  <OverlayTrigger
                                    overlay={
                                      <StyledTooltip
                                        id={`customerEmails.${index}.default1`}
                                      >
                                        Set as default email
                                      </StyledTooltip>
                                    }
                                  >
                                    <span className="d-inline-block">
                                      <FormikCheckBox
                                        name={`customerEmails.${index}.default1`}
                                        onChange={() => {
                                          values.customerEmails =
                                            values?.customerEmails?.map(
                                              (i) => ({
                                                ...i,
                                                default1: 'false',
                                              }),
                                            );
                                          setFieldValue(
                                            `customerEmails.${index}.default1`,
                                            'true',
                                          );
                                        }}
                                        id="customerEmails"
                                        value={
                                          item?.default1 === 'true'
                                            ? true
                                            : false
                                        }
                                      />
                                    </span>
                                  </OverlayTrigger>
                                  <FormikInputField
                                    name={`customerEmails.${index}.email`}
                                    id={`customerEmails.${index}.email`}
                                    placeholder="Enter email"
                                  />
                                </div>
                                {values?.customerEmails?.length > 1 && (
                                  <StyledButton
                                    className="py-0"
                                    variant="iconBackground"
                                    icon={<TrashIcon />}
                                    type={'button'}
                                    onClick={() => {
                                      arrayHelpers.remove(index);
                                    }}
                                  />
                                )}
                              </div>
                            </Col>
                          ))}
                      </Row>
                      <h6
                        className="text-primary fw-bold"
                        role="button"
                        onClick={() =>
                          arrayHelpers.insert(
                            values?.customerEmails?.length,
                            '',
                          )
                        }
                      >
                        + Add another email
                      </h6>
                    </Col>
                  )}
                />
                <Col sm={6} className="mb-2">
                  <label htmlFor={`address`} className="form-label">
                    Address1
                  </label>
                  <FormikAsyncMap name="address" placeholder="Enter address " />
                </Col>

                <Col sm={6} className="mb-2">
                  <label htmlFor={`address2`} className="form-label">
                    Address 2
                  </label>
                  <FormikInputField
                    name={`address2`}
                    id={`address2`}
                    placeholder="Enter address2"
                  />
                </Col>

                <Col sm={12} className="mb-2">
                  <label htmlFor={`notes`} className="form-label">
                    Notes
                  </label>
                  <FormikInputField
                    name={`notes`}
                    id={`notes`}
                    placeholder="Enter notes"
                  />
                </Col>
              </Row>
              <div className="d-flex justify-content-end m-4 mt-3">
                <div className="d-flex gap-3">
                  <StyledButton
                    variant={'white'}
                    className="mb-2 border-0 fw-bold"
                    onClick={() => navigate('/customers')}
                  >
                    Cancel
                  </StyledButton>
                  <OverlayTrigger
                    overlay={
                      id && !isNaN(id) ? (
                        has_permission_to_update_customer ? (
                          <></>
                        ) : (
                          <StyledTooltips>
                            You do not have permission to edit customer details
                          </StyledTooltips>
                        )
                      ) : (
                        <></>
                      )
                    }
                  >
                    <div>
                      <StyledButton
                        className="mb-2"
                        type="submit"
                        loading={loading || submitLoading}
                        disabled={
                          loading ||
                          submitLoading ||
                          (id &&
                            !isNaN(id) &&
                            !has_permission_to_update_customer)
                        }
                      >
                        Save
                      </StyledButton>
                    </div>
                  </OverlayTrigger>
                </div>
              </div>
            </ShopStyled.BootstrapCard>
          </Form>
        )}
      </Formik>
    </ShopStyled.PageWrap>
  );
};

const StyledTooltip = styled(Tooltip)`
  position: absolute !important;
`;

export default CreateCustomer;
